import React, { useState, useEffect } from 'react';

const PuzzleUnlock = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [currentPuzzle, setCurrentPuzzle] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [doors, setDoors] = useState([]);
  const [unlockedDoors, setUnlockedDoors] = useState(0);
  const [timeLeft, setTimeLeft] = useState(120);

  const generatePuzzle = (lvl) => {
    const puzzleTypes = [
      // Arithmetic
      () => {
        const a = Math.floor(Math.random() * 20) + lvl * 5;
        const b = Math.floor(Math.random() * 20) + lvl * 5;
        return { question: `${a} + ${b} = ?`, answer: a + b, type: 'arithmetic' };
      },
      () => {
        const a = Math.floor(Math.random() * 15) + lvl * 3;
        const b = Math.floor(Math.random() * 12) + 2;
        return { question: `${a} × ${b} = ?`, answer: a * b, type: 'arithmetic' };
      },
      // Algebra
      () => {
        const x = Math.floor(Math.random() * 10) + 1;
        const b = Math.floor(Math.random() * 20) + 5;
        const result = x + b;
        return { question: `x + ${b} = ${result}, x = ?`, answer: x, type: 'algebra' };
      },
      () => {
        const x = Math.floor(Math.random() * 10) + 2;
        const b = Math.floor(Math.random() * 5) + 2;
        const result = x * b;
        return { question: `${b}x = ${result}, x = ?`, answer: x, type: 'algebra' };
      },
      // Geometry
      () => {
        const side = Math.floor(Math.random() * 10) + 3;
        return { question: `Perimeter of square with side ${side}?`, answer: side * 4, type: 'geometry' };
      },
      () => {
        const l = Math.floor(Math.random() * 10) + 3;
        const w = Math.floor(Math.random() * 10) + 3;
        return { question: `Area of rectangle ${l}×${w}?`, answer: l * w, type: 'geometry' };
      }
    ];

    const generator = puzzleTypes[Math.floor(Math.random() * Math.min(puzzleTypes.length, 2 + lvl))];
    return generator();
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setUnlockedDoors(0);
    setTimeLeft(120);
    setUserAnswer('');
    initializeDoors(1);
  };

  const initializeDoors = (lvl) => {
    const doorCount = Math.min(5 + lvl, 10);
    const newDoors = Array(doorCount).fill(null).map((_, i) => ({
      id: i,
      locked: true,
      puzzle: generatePuzzle(lvl)
    }));
    setDoors(newDoors);
    setCurrentPuzzle(newDoors[0].puzzle);
  };

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            setGameState('gameover');
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  const checkAnswer = () => {
    const answer = parseInt(userAnswer);
    if (answer === currentPuzzle.answer) {
      setScore(s => s + 100 * level);
      setUnlockedDoors(u => u + 1);
      setTimeLeft(t => t + 10);
      
      const nextLockedDoor = doors.find(d => d.locked);
      if (nextLockedDoor) {
        nextLockedDoor.locked = false;
        const nextDoor = doors.find(d => d.locked);
        if (nextDoor) {
          setCurrentPuzzle(nextDoor.puzzle);
        } else {
          // Level complete
          setLevel(l => l + 1);
          setTimeout(() => initializeDoors(level + 1), 1000);
        }
      }
      setUserAnswer('');
    } else {
      setUserAnswer('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 p-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-yellow-300">
          🔐 Puzzle Unlock 🚪
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🔐</div>
            <h2 className="text-3xl font-bold mb-4">Unlock the Doors!</h2>
            <p className="text-xl mb-8 text-gray-600">
              Solve math puzzles to unlock doors<br/>
              Progress through levels!
            </p>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              START GAME
            </button>
          </div>
        )}

        {gameState === 'playing' && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between items-center">
              <div className="text-xl font-bold">Score: {score}</div>
              <div className="text-xl font-bold">Level: {level}</div>
              <div className="text-xl font-bold">Unlocked: {unlockedDoors}/{doors.length}</div>
              <div className={`text-xl font-bold ${timeLeft < 30 ? 'text-red-600 animate-pulse' : 'text-green-600'}`}>
                Time: {timeLeft}s
              </div>
            </div>

            {/* Doors Display */}
            <div className="grid grid-cols-5 gap-4 mb-6">
              {doors.map((door) => (
                <div
                  key={door.id}
                  className={`aspect-square rounded-lg flex items-center justify-center text-4xl transition-all ${
                    door.locked
                      ? 'bg-gray-700 text-gray-400'
                      : 'bg-green-500 text-white animate-pulse'
                  }`}
                >
                  {door.locked ? '🔒' : '🔓'}
                </div>
              ))}
            </div>

            {/* Current Puzzle */}
            {currentPuzzle && (
              <div className="bg-white rounded-lg p-8 shadow-xl">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-gray-800 mb-4">
                    {currentPuzzle.question}
                  </div>
                  <input
                    type="number"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && checkAnswer()}
                    className="w-64 p-4 text-2xl text-center border-4 border-purple-500 rounded-lg"
                    placeholder="Your answer"
                    autoFocus
                  />
                </div>
                <button
                  onClick={checkAnswer}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-4 rounded-lg text-xl font-bold hover:scale-105 transform transition"
                >
                  Unlock Door 🔓
                </button>
              </div>
            )}
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🏆</div>
            <h2 className="text-4xl font-bold mb-4">Game Over!</h2>
            <div className="text-3xl mb-4 text-purple-600 font-bold">Score: {score}</div>
            <div className="text-2xl mb-8 text-gray-600">Doors Unlocked: {unlockedDoors}</div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              PLAY AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PuzzleUnlock;
